import React, { FC, useState } from 'react';
import { Button, Icon, Tab, TabProps } from 'semantic-ui-react';
import { FlexAlign, FlexJustify } from '@src/types';
import { Flexbox } from '@src/views/containers';
import OfferingTab from './OfferingTab';
import type { OfferingOutline } from './ClientOutlineTypes';

interface OfferingTabMenuProps {
    clientId: string;
    sowId: string;
    offerings: OfferingOutline[];
    onOpenAddOfferingDialog?: () => void;
    disableAddOffering?: boolean;
    emptyMessage?: string;
}

const OfferingTabMenu: FC<OfferingTabMenuProps> = ( {
    clientId,
    sowId,
    offerings,
    onOpenAddOfferingDialog,
    disableAddOffering,
    emptyMessage = 'No Offerings for this SOW yet.',
} ) => {
    const [ activeIndex, setActiveIndex ] = useState( 0 );
    const canAddOffering = !disableAddOffering && !!onOpenAddOfferingDialog;

    if( offerings.length === 0 ){
        return (
            <Flexbox
                align={ FlexAlign.CENTER }
                justify={ FlexJustify.START }
                className={ 'offering-empty-state' }
            >
                <span className={ 'offering-empty-state-text' }>{ emptyMessage }</span>
                <Button
                    type={ 'button' }
                    className={ 'dash-add-btn' }
                    onClick={ onOpenAddOfferingDialog }
                    disabled={ !canAddOffering }
                >
                    <Icon name={ 'plus' } />
                    Add offering
                </Button>
            </Flexbox>
        );
    }

    const panes: TabProps[ 'panes' ] = offerings.map( offering => ( {
        menuItem: offering.OfferingName,
        render: () => (
            <Tab.Pane attached={ false }>
                <OfferingTab clientId={ clientId } sowId={ sowId } offering={ offering } />
            </Tab.Pane>
        ),
    } ) );

    // The add control is the last item in the strip so it sits right after the
    // offerings it adds to (and scrolls with them).
    panes.push( {
        menuItem: {
            key: '__add-offering',
            className: 'consultant-tab-menu-add-item offering-add-item',
            title: 'Add Offering',
            'aria-label': 'Add Offering',
            disabled: !canAddOffering,
            content: (
                <>
                    <Icon name={ 'plus' } />
                    Add offering
                </>
            ),
        },
        render: () => null,
    } );

    // Clamp in case an offering was removed while a later tab was selected.
    const safeActiveIndex = Math.min( activeIndex, offerings.length - 1 );

    const handleTabChange = ( _event: React.MouseEvent<HTMLDivElement>, data: TabProps ) => {
        const newIndex = typeof data.activeIndex === 'number' ? data.activeIndex : Number( data.activeIndex );

        // The trailing add item opens the dialog instead of becoming a tab.
        if( newIndex === offerings.length ){
            if( canAddOffering ) onOpenAddOfferingDialog?.();
            return;
        }

        setActiveIndex( newIndex );
    };

    return (
        <div className={ 'consultant-tab-menu-with-action consultant-tab-menu-with-action-offering' }>
            <Tab
                menu={ { secondary: true, pointing: true } }
                panes={ panes }
                renderActiveOnly
                activeIndex={ safeActiveIndex }
                onTabChange={ handleTabChange }
            />
        </div>
    );
};

export default OfferingTabMenu;
